/**
 * Landing Pages List
 * Dashboard page to manage lead capture landing pages
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getLandingPages,
  deleteLandingPage,
} from "../services/landingPage.service";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  PlusIcon,
  TrashIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import type { LandingPage } from "../types/landingPage.types";

export default function LandingPages() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const {
    data: landingPages,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["landing-pages"],
    queryFn: getLandingPages,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteLandingPage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landing-pages"] });
    },
  });

  const handleDelete = (e: React.MouseEvent, lp: LandingPage) => {
    e.stopPropagation();
    if (
      window.confirm(
        `Delete landing page "${lp.title}"? This cannot be undone.`,
      )
    ) {
      deleteMutation.mutate(lp.id);
    }
  };

  const handleCopyUrl = (e: React.MouseEvent, slug: string) => {
    e.stopPropagation();
    const url = `${window.location.origin}/lp/${slug}`;
    navigator.clipboard.writeText(url);
    setCopiedId(slug);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading landing pages..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Failed to load landing pages. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Landing Pages</h1>
          <p className="text-sm text-gray-500 mt-1">
            Create lead capture pages with forms that auto-create contacts and
            segments
          </p>
        </div>
        <button
          onClick={() => navigate("/landing-pages/create")}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <PlusIcon className="w-4 h-4" />
          Create Landing Page
        </button>
      </div>

      {/* Table */}
      {!landingPages || landingPages.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500 mb-4">No landing pages yet.</p>
          <button
            onClick={() => navigate("/landing-pages/create")}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <PlusIcon className="w-4 h-4" />
            Create your first landing page
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {landingPages.map((lp) => (
                <tr
                  key={lp.id}
                  onClick={() => navigate(`/landing-pages/${lp.id}`)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {lp.title}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                    /lp/{lp.slug}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        lp.is_published
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {lp.is_published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(lp.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={(e) => handleCopyUrl(e, lp.slug)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                        title="Copy URL"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      </button>
                      {copiedId === lp.slug && (
                        <span className="text-xs text-green-600">Copied!</span>
                      )}
                      <button
                        onClick={(e) => handleDelete(e, lp)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
